import { LitElement, html, css, utils, styles } from '@hh/global.js'

const { getCart, updateCart } = utils

/**
 * Proof UI shows only when cart has full customization.
 * Plate and legacy lines are ignored, including their uploaded files.
 *
 * Default _proof:
 * - any full + _canvas → approved
 * - else full + _uploaded_file → unapproved
 * - else full → unapproved
 */
const isFullItem = (item) =>
  item?.properties?._customization_method === 'full'

const isCartProofAvailable = (cart) => cart.items.some(isFullItem)

const resolveProofDefault = (cart) => {
  const fullItems = cart.items.filter(isFullItem)
  if (!fullItems.length) return null

  if (fullItems.some((item) => !!item?.properties?._canvas)) {
    return 'approved'
  }

  const hasUploadedFile = fullItems.some(
    (item) => !!item?.properties?._uploaded_file
  )
  if (hasUploadedFile) return 'unapproved'

  return 'unapproved'
}

class HHCartProof extends LitElement {
  constructor() {
    super()

    this.proofInputs = this.querySelectorAll('.js-proof-input')
    this.handleProofChange = this.handleProofChange.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.checkCartProof()
    this.proofInputs.forEach((input) =>
      input.addEventListener('input', this.handleProofChange)
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.proofInputs.forEach((input) =>
      input.removeEventListener('input', this.handleProofChange)
    )
  }

  async handleProofChange(e) {
    const { value: proofStatus } = e.target

    await updateCart({
      attributes: {
        _proof: proofStatus,
      },
    })
  }

  syncProofInputs(proof) {
    this.proofInputs.forEach((input) => {
      input.checked = input.value === proof
    })
  }

  async checkCartProof() {
    const cart = await getCart()
    const cartProofAvailable = isCartProofAvailable(cart)

    if (!cartProofAvailable) {
      this.setAttribute('hidden', '')
      this.style.display = 'none'

      if (cart.attributes?._proof) {
        await updateCart({
          attributes: {
            _proof: '',
          },
        })
      }
      return
    }

    this.removeAttribute('hidden')
    this.style.display = ''

    const preferred = resolveProofDefault(cart)
    const current = cart.attributes?._proof

    if (preferred && preferred !== current) {
      this.syncProofInputs(preferred)
      await updateCart({
        attributes: {
          _proof: preferred,
        },
      })
    }
  }

  render() {
    return html`<slot></slot>`
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([hidden]) {
        display: none !important;
      }
    `,
  ]
}

customElements.define('hh-cart-proof', HHCartProof)